import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button, TextField, Link, Typography, IconButton, InputAdornment, Menu, MenuItem, CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ArrowForward from "@material-ui/icons/ArrowForward";
import Language from "@material-ui/icons/Language";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import { DEFAULT_APP_NAME } from "../../design/theme";

const languages = [{ value: "pt-BR", label: "Português" }, { value: "en", label: "English" }, { value: "es", label: "Español" }, { value: "ar", label: "عربي" }];

const Login = () => {
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const { handleLogin, loading } = useContext(AuthContext);
  const { getPublicSetting } = useSettings();
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    if (window.__whitelabelBackendUrl) return () => { active = false; };
    const companyId = new URLSearchParams(window.location.search).get("companyId");
    getPublicSetting("userCreation", companyId).then((value) => { if (active) setAllowSignup(value === "enabled"); }).catch(() => {});
    getPublicSetting("enabledLanguages", companyId).then((value) => {
      try { const parsed = JSON.parse(value); if (active && Array.isArray(parsed)) setEnabledLanguages(parsed); } catch (_) {}
    }).catch(() => {});
    return () => { active = false; };
    // Public company settings load once for this login URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || loading) return;
    setSubmitting(true);
    try { await handleLogin({ ...user, email: user.email.trim() }); } finally { setSubmitting(false); }
  };
  const changeLanguage = (value) => {
    i18n.changeLanguage(value);
    localStorage.setItem("language", value);
    setLanguageAnchor(null);
    window.location.reload();
  };

  return (
    <main className="enterprise-login">
      <section className="login-story" aria-label="Apresentação da plataforma">
        <img className="login-brand" style={window.__whitelabelBackendUrl ? { filter: "none" } : undefined} src={theme.calculatedLogoDark()} alt={theme.appName || DEFAULT_APP_NAME} />
        <div className="login-story-content">
          <span className="eyebrow">RELACIONAMENTO. OPERAÇÃO. RESULTADOS.</span>
          <h1>Conexões que<br />movem o seu negócio<span>.</span></h1>
          <p>Uma visão integrada da sua operação. Mais contexto para a equipe, mais proximidade com cada cliente.</p>
          <div className="login-workflow" aria-label="Atendimento conectado à sua equipe">
            <div className="workflow-heading"><span className="workflow-dot" />Sua operação, em sintonia</div>
            <div className="workflow-step"><span>01</span><div><strong>Conversas centralizadas</strong><small>Todos os canais em um só lugar</small></div><ArrowForward fontSize="small" /></div>
            <div className="workflow-step"><span>02</span><div><strong>Equipes conectadas</strong><small>Contexto para cada atendimento</small></div><ArrowForward fontSize="small" /></div>
            <div className="workflow-step"><span>03</span><div><strong>Decisões mais claras</strong><small>Indicadores que guiam a operação</small></div><ArrowForward fontSize="small" /></div>
          </div>
        </div>
        <div className="login-story-footer">Tecnologia a serviço de boas relações.</div>
      </section>
      <section className="login-access">
        <div className="login-preferences">
          <Button startIcon={<Language fontSize="small" />} onClick={(event) => setLanguageAnchor(event.currentTarget)} aria-label="Selecionar idioma">{languages.find((lang) => lang.value === i18n.language)?.label || "Português"}</Button>
          <Menu anchorEl={languageAnchor} open={Boolean(languageAnchor)} onClose={() => setLanguageAnchor(null)} getContentAnchorEl={null}>
            {languages.filter((lang) => enabledLanguages.includes(lang.value)).map((lang) => <MenuItem key={lang.value} onClick={() => changeLanguage(lang.value)}>{lang.label}</MenuItem>)}
          </Menu>
          <IconButton onClick={colorMode.toggleColorMode} aria-label={theme.mode === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}>{theme.mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}</IconButton>
        </div>
        <div className="login-form-wrapper">
          <img className="login-mobile-brand" src={theme.mode === "dark" ? theme.calculatedLogoDark() : theme.calculatedLogoLight()} alt={theme.appName} />
          <span className="eyebrow">SEU AMBIENTE DE TRABALHO</span>
          <Typography component="h2" variant="h4">Bem-vindo de volta</Typography>
          <Typography className="login-description" color="textSecondary">Entre com sua conta para continuar.</Typography>
          <form onSubmit={handleSubmit} className="login-form">
            <TextField variant="outlined" margin="normal" required fullWidth id="email" label={i18n.t("login.form.email")} name="email" type="email" value={user.email} onChange={(event) => setUser({ ...user, email: event.target.value })} autoComplete="username" autoFocus />
            <TextField variant="outlined" margin="normal" required fullWidth id="password" label={i18n.t("login.form.password")} name="password" type={showPassword ? "text" : "password"} value={user.password} onChange={(event) => setUser({ ...user, password: event.target.value })} autoComplete="current-password" InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
            <Button type="submit" fullWidth variant="contained" color="primary" disabled={submitting || loading} className="login-submit" endIcon={submitting || loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForward fontSize="small" />}>{i18n.t("login.buttons.submit")}</Button>
            {allowSignup && <div className="login-register"><Link component={RouterLink} to="/signup">{i18n.t("login.buttons.register")}</Link></div>}
          </form>
          <p className="login-access-note">Seu próximo atendimento começa com uma boa conexão.</p>
        </div>
        <footer className="login-footer">{theme.appName || DEFAULT_APP_NAME}<span>Plataforma de relacionamento</span></footer>
      </section>
    </main>
  );
};
export default Login;
